export const C = {
  primary: "#254b8c",      // Executive Insight navy — sidebar, CTAs
  primaryDark: "#013374",  // hover / pressed
  primaryBg: "#e8eef9",    // light navy tint — active/hover surfaces
  success: "#137333",     // WCAG AA: 5.3:1 on successBg, 6.0:1 for white text on it
  successBg: "#e6f4ea",
  successLight: "#90d67f",
  warning: "#9a5400",      // WCAG AA: 5.4:1 on warningBg
  warningBg: "#fef7e0",
  warningLight: "#ffcc85",
  danger: "#c5221f",       // WCAG AA: 4.9:1 on dangerBg, 5.8:1 for white text on it
  dangerBg: "#fce8e6",
  dangerLight: "#fabcb3",
  info: "#0071b0",         // WCAG AA: 5.3:1 on white, 4.7:1 on infoBg
  infoBg: "#e6f5fd",
  purple: "#6f42c1",
  purpleBg: "#f0e6ff",
  purpleBorder: "#6f42c133", // 20% purple — accent borders on virement/Xero cards
  gray50: "#f9f9ff",       // app background
  gray100: "#f4f3f9",
  gray200: "#dad9df",      // borders / dividers
  gray300: "#cbd0dd",
  gray400: "#9fa6bc",
  gray500: "#636e8a",      // WCAG AA: 5.1:1 on white (was #8a94ad at 3.0:1)
  gray600: "#434750",      // meta text
  gray700: "#525b75",
  gray800: "#3e465b",
  gray900: "#31374a",
  gray1100: "#1a1b20",     // primary text
  white: "#fff",
};

export const shadow = "0 4px 20px rgba(0,0,0,0.05)";

export const radius = { sm: 4, control: 6, input: 6, card: 10, pill: 9999 };

// Type scale — sizes measured from actual usage across the app.
export const text = {
  micro: 10,  // uppercase micro-labels (sidebar sections)
  xs: 11,     // pills, uppercase table headers
  sm: 12,     // meta text, form labels
  md: 13,     // body, controls
  lg: 14,     // emphasized body, list titles
  h3: 16,     // small headings
  h2: 18,     // section headings
  h1: 20,     // page titles
  metric: 32, // KPI values
};

export const font = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// The one money formatter (UX-09/UX-46): every budget figure rounds to cents
// the same way, so no surface shows a differently-rounded "available".
export const fmt = (n) => {
  const v = Math.round(((n || 0) + Number.EPSILON) * 100) / 100;
  const s = Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
  return v < 0 ? `-$${s}` : `$${s}`;
};

export const STATUS_COLORS = {
  DRAFT: { bg: C.gray100, text: C.gray700 },
  PENDING_APPROVAL: { bg: C.warningBg, text: C.warning },
  APPROVED: { bg: C.successBg, text: C.success },
  REJECTED: { bg: C.dangerBg, text: C.danger },
  // Returned to the requestor after a rejection — needs their action, so it
  // must read differently from DRAFT (never submitted) everywhere.
  RETURNED: { bg: C.dangerBg, text: C.danger },
  ISSUED: { bg: C.primaryBg, text: C.primary },
  FULFILLED: { bg: C.successBg, text: C.success },
  MATCHED: { bg: C.successBg, text: C.success },
  PUSHED_TO_XERO: { bg: C.purpleBg, text: C.purple },
  PAID: { bg: C.successBg, text: C.success },
  UNPAID: { bg: C.dangerBg, text: C.danger },
  WITHIN_BUDGET: { bg: C.successBg, text: C.success },
  OVER_BUDGET: { bg: C.dangerBg, text: C.danger },
  NEAR_LIMIT: { bg: C.warningBg, text: C.warning },
  PENDING_VIREMENT: { bg: C.purpleBg, text: C.purple },
  EXECUTED: { bg: C.successBg, text: C.success },
  PENDING_ACTION: { bg: C.warningBg, text: C.warning },
  PENDING_ACK: { bg: C.warningBg, text: C.warning },
  IN_EVALUATION: { bg: C.primaryBg, text: C.primary },
  CONSOLIDATED: { bg: C.purpleBg, text: C.purple },
  AWARDED: { bg: C.successBg, text: C.success },
  CONVERTED: { bg: C.successBg, text: C.success },
  CANCELLED: { bg: C.gray100, text: C.gray700 },
};

export const statusLabel = (s) =>
  s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "";

// Data-scope enum → plain words for the identity chip (UX-20): "INVOLVED" is
// internal jargon; say what the scope means for what the user sees.
export const scopeLabel = (dataScope) =>
  dataScope === "ALL" ? "sees all records" : "sees own & assigned records";